import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Home, Brain, PlayCircle, Lightbulb, CheckSquare,
  AlertTriangle, Timer, BookOpen, Trophy, Sparkles,
  Target, Mic, ChevronRight, Star, Award
} from 'lucide-react';
// Sub-components
import InterviewQuizPractice from './InterviewQuizPractice';
import InterviewFlashcards from './InterviewFlashcards';
import InterviewSelfRecord from './InterviewSelfRecord';
import InterviewChecklist from './InterviewChecklist';
import InterviewMistakes from './InterviewMistakes';
import InterviewTips from './InterviewTips';
import InterviewTimedPractice from './InterviewTimedPractice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InterviewPracticeHub() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(null);
  const [practiceStats, setPracticeStats] = useState({
    quizCompleted: 0,
    flashcardsReviewed: 0,
    recordingsCount: 0,
    checklistProgress: 0,
    totalPracticeTime: 0
  });

  const features = [
    {
      id: 'quiz',
      title: 'Soru-Cevap Pratiği',
      subtitle: 'Havayoluna göre gerçek mülakat soruları',
      icon: Brain,
      color: 'from-blue-500 to-indigo-600',
      emoji: '🧠',
      stats: `${practiceStats.quizCompleted} quiz tamamlandı`
    },
    {
      id: 'flashcards',
      title: 'Flashcard\'lar',
      subtitle: 'Hızlı tekrar ve ezber kartları',
      icon: BookOpen,
      color: 'from-emerald-500 to-teal-600',
      emoji: '📚',
      stats: `${practiceStats.flashcardsReviewed} kart çalışıldı`
    },
    {
      id: 'self-record',
      title: 'Kendini Kaydet',
      subtitle: 'Sesli/görüntülü pratik yap',
      icon: Mic,
      color: 'from-purple-500 to-pink-600',
      emoji: '🎙️',
      stats: `${practiceStats.recordingsCount} kayıt`
    },
    {
      id: 'timed',
      title: 'Zamanlı Pratik',
      subtitle: 'Süre baskısı altında cevap ver',
      icon: Timer,
      color: 'from-orange-500 to-red-600',
      emoji: '⏱️',
      stats: 'Cevap süreni geliştir'
    },
    {
      id: 'checklist',
      title: 'Hazırlık Kontrol Listesi',
      subtitle: 'Mülakat öncesi yapılacaklar',
      icon: CheckSquare,
      color: 'from-green-500 to-emerald-600',
      emoji: '✅',
      stats: `%${practiceStats.checklistProgress} tamamlandı`
    },
    {
      id: 'mistakes',
      title: 'Yapılan Hatalar',
      subtitle: 'Kaçınılması gereken yaygın hatalar',
      icon: AlertTriangle,
      color: 'from-red-500 to-rose-600',
      emoji: '⚠️',
      stats: 'Hatalardan öğren'
    },
    {
      id: 'tips',
      title: 'Havayolu İpuçları',
      subtitle: 'Her havayolu için özel tavsiyeler',
      icon: Lightbulb,
      color: 'from-yellow-500 to-amber-600',
      emoji: '💡',
      stats: '7 havayolu ipucu'
    },
    {
      id: 'progress',
      title: 'İlerleme Takibi',
      subtitle: 'Performansını izle',
      icon: Target,
      color: 'from-cyan-500 to-blue-600',
      emoji: '📊',
      stats: 'Grafikler ve analizler'
    }
  ];

  // If a feature is active, render that component
  if (activeFeature) {
    const FeatureComponents = {
      'quiz': InterviewQuizPractice,
      'flashcards': InterviewFlashcards,
      'self-record': InterviewSelfRecord,
      'timed': InterviewTimedPractice,
      'checklist': InterviewChecklist,
      'mistakes': InterviewMistakes,
      'tips': InterviewTips,
      'progress': null // Will show inline progress
    };

    const ActiveComponent = FeatureComponents[activeFeature];

    if (activeFeature === 'progress') {
      return (
        <ProgressTracker 
          stats={practiceStats} 
          onBack={() => setActiveFeature(null)} 
        />
      );
    }

    if (ActiveComponent) {
      return <ActiveComponent onBack={() => setActiveFeature(null)} />;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 pt-6 pb-8 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate('/interview-prep')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Mülakat Merkezi</span>
            </button>
            
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
              <Trophy className="w-4 h-4 text-yellow-300" />
              <span className="text-white font-bold text-sm">Pratik Merkezi</span>
            </div>
          </div>

          <div className="text-center text-white mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-yellow-300" />
              <h1 className="text-2xl font-bold">İnteraktif Mülakat Pratiği</h1>
              <Sparkles className="w-6 h-6 text-yellow-300" />
            </div>
            <p className="text-purple-100 text-sm">8 farklı özellik ile mülakata %100 hazır ol!</p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-white">{practiceStats.quizCompleted}</div>
              <div className="text-xs text-purple-100">Quiz</div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-white">{practiceStats.flashcardsReviewed}</div>
              <div className="text-xs text-purple-100">Flashcard</div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-white">{practiceStats.recordingsCount}</div>
              <div className="text-xs text-purple-100">Kayıt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <PlayCircle className="w-5 h-5 text-indigo-600" />
          Pratik Özellikleri
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card 
                key={feature.id}
                className="overflow-hidden transition-all duration-300 border-0 shadow-md cursor-pointer hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                onClick={() => setActiveFeature(feature.id)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center">
                    <div className={`w-20 h-24 bg-gradient-to-br ${feature.color} flex items-center justify-center`}>
                      <span className="text-3xl">{feature.emoji}</span>
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{feature.title}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{feature.subtitle}</p>
                          <p className="text-xs text-indigo-600 mt-2 font-medium">{feature.stats}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Motivational Card */}
        <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Başarı İpucu</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Her gün en az <strong>1 quiz</strong> çöz, <strong>10 flashcard</strong> tekrar et ve 
                  <strong> 1 ses kaydı</strong> yap. Düzenli pratik = Mülakat başarısı!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Progress Tracker Component
function ProgressTracker({ stats, onBack }) {
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-gradient-to-br from-cyan-600 to-blue-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">Mülakat Merkezi</span>
          </button>
          
          <h1 className="text-2xl font-bold text-white text-center">📊 İlerleme Takibi</h1>
          <p className="text-cyan-100 text-center text-sm mt-1">Pratik performansın</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6 space-y-4">
        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-gray-800 mb-4">Genel İstatistikler</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.quizCompleted}</div>
                <div className="text-sm text-gray-600">Quiz Tamamlandı</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{stats.flashcardsReviewed}</div>
                <div className="text-sm text-gray-600">Flashcard Çalışıldı</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">{stats.recordingsCount}</div>
                <div className="text-sm text-gray-600">Ses Kaydı</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-orange-600">{stats.totalPracticeTime}</div>
                <div className="text-sm text-gray-600">Dakika Pratik</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-gray-800 mb-4">Hazırlık Seviyesi</h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Temel Sorular</span>
                  <span>70%</span>
                </div>
                <Progress value={70} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Havayolu Bilgisi</span>
                  <span>50%</span>
                </div>
                <Progress value={50} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Senaryo Soruları</span>
                  <span>30%</span>
                </div>
                <Progress value={30} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎯</span>
              <div>
                <h4 className="font-semibold text-gray-800">Günlük Hedef</h4>
                <p className="text-sm text-gray-600">Bugün 3 quiz daha çöz ve hedefe ulaş!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
